/**
 * @fileoverview Market page — data loading
 * @module services/marketService
 *
 * Keeps raw Firestore reads out of UI components. Two data sources are
 * combined here:
 *   1. Public data (game_slates + races/runners) — read directly, same as
 *      the Showdowns page, since these collections allow public reads.
 *   2. Private crowd-ownership/ROI data (player_entries/race_allocations) —
 *      MUST go through the `getMarketAggregates` callable (showdown-functions/
 *      marketAggregates.js), because Firestore rules only let a player read
 *      their own entry. See that file's header comment for the full
 *      rationale. Never attempt to query player_entries for other users
 *      from the client — it will always be denied.
 *
 * TODO: Move market analytics to scheduled aggregation docs before heavy
 * user scale (see showdown-functions/marketAggregates.js).
 */

import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { getShowdownDisplayState, toMs } from '../utils/showdownStatus';
import { buildRunnerMarketRows } from '../utils/marketAnalytics';

// Recommended scope (section 4): active/upcoming + a capped recent-completed
// window, so Market never scans the platform's full history.
const MAX_COMPLETED_SLATES = 12;
const MAX_TOTAL_SLATES = 24;

/**
 * Loads and classifies game_slates into the Market v1 scope:
 * every active/upcoming slate, plus the most recent completed ones.
 */
async function fetchScopedSlates() {
  const snap = await getDocs(collection(db, 'game_slates'));
  const slates = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const nonArchived = slates.filter(s => s.archived !== true);

  const activeUpcoming = [];
  const completed = [];

  nonArchived.forEach(s => {
    const state = getShowdownDisplayState(s);
    if (!state) return;
    if (state.tabBucket === 'live' || state.tabBucket === 'upcoming') activeUpcoming.push(s);
    else if (state.tabBucket === 'completed') completed.push(s);
  });

  completed.sort((a, b) => (toMs(b.meetingDate) || 0) - (toMs(a.meetingDate) || 0));

  return [...activeUpcoming, ...completed.slice(0, MAX_COMPLETED_SLATES)].slice(0, MAX_TOTAL_SLATES);
}

/** Loads races (with embedded runners[]) for every scoped slate, in parallel. */
async function fetchRacesForSlates(slates) {
  const perSlate = await Promise.all((slates || []).map(async (slate) => {
    try {
      const racesSnap = await getDocs(collection(db, 'game_slates', slate.id, 'races'));
      return racesSnap.docs.map(d => ({ id: d.id, slateId: slate.id, ...d.data() }));
    } catch (err) {
      console.error(`[marketService] Failed to load races for slate ${slate.id}:`, err);
      return [];
    }
  }));
  return perSlate.flat();
}

/** Calls the server-side ownership/ROI aggregation — see file header. */
async function fetchMarketAggregates(slateIds) {
  const empty = { generatedAt: Date.now(), entriesBySlate: {}, runners: [] };
  if (!slateIds || slateIds.length === 0) return empty;

  try {
    const fn = httpsCallable(functions, 'getMarketAggregates');
    const res = await fn({ slateIds });
    return res?.data || empty;
  } catch (err) {
    console.error('[marketService] getMarketAggregates failed:', err);
    return { ...empty, error: true };
  }
}

/**
 * Loads and normalizes all Market v1 data in one call.
 *
 * @returns {Promise<{
 *   slates: Array, races: Array, runners: Array,
 *   generatedAt: number, aggregatesErrored: boolean
 * }>}
 */
export async function loadMarketData() {
  const slates = await fetchScopedSlates();
  const races = await fetchRacesForSlates(slates);
  const aggregates = await fetchMarketAggregates(slates.map(s => s.id));

  const runners = buildRunnerMarketRows({
    slates,
    races,
    aggregateRows: aggregates.runners || [],
    entriesBySlate: aggregates.entriesBySlate || {},
  });

  return {
    slates,
    races,
    runners,
    generatedAt: aggregates.generatedAt || Date.now(),
    aggregatesErrored: !!aggregates.error,
  };
}
